/**
 * Style Converter Utility
 * 
 * Converts PageBuilder Style objects to MUI sx props
 */

import { Style } from '../types/page-builder.types';
import { SxProps, Theme } from '@mui/material/styles';

/**
 * Converts a PageBuilder Style object to MUI sx props
 */
export function convertStyleToSx(style?: Style): SxProps<Theme> {
  if (!style) return {};

  const sx: any = {};

  // Padding - convert pixel values to px strings for proper rendering
  if (style.padding !== undefined) {
    if (typeof style.padding === 'number') {
      sx.p = `${style.padding}px`;
    } else if (typeof style.padding === 'object') {
      if (style.padding.top !== undefined) sx.pt = `${style.padding.top}px`;
      if (style.padding.right !== undefined) sx.pr = `${style.padding.right}px`;
      if (style.padding.bottom !== undefined) sx.pb = `${style.padding.bottom}px`;
      if (style.padding.left !== undefined) sx.pl = `${style.padding.left}px`;
    }
  }

  // Margin - convert pixel values to px strings for proper rendering
  if (style.margin !== undefined) {
    if (typeof style.margin === 'number') {
      sx.m = `${style.margin}px`;
    } else if (typeof style.margin === 'object') {
      if (style.margin.top !== undefined) sx.mt = `${style.margin.top}px`;
      if (style.margin.right !== undefined) sx.mr = `${style.margin.right}px`;
      if (style.margin.bottom !== undefined) sx.mb = `${style.margin.bottom}px`;
      if (style.margin.left !== undefined) sx.ml = `${style.margin.left}px`;
    }
  }

  // Background
  if (style.backgroundColor) {
    sx.backgroundColor = style.backgroundColor;
  }
  if (style.bg) {
    // Legacy support
    sx.backgroundColor = style.bg;
  }
  if (style.backgroundImage) {
    sx.backgroundImage = style.backgroundImage.startsWith('url(') 
      ? style.backgroundImage 
      : `url(${style.backgroundImage})`;
  }
  if (style.backgroundSize) {
    sx.backgroundSize = style.backgroundSize;
  }
  if (style.backgroundPosition) {
    sx.backgroundPosition = style.backgroundPosition;
  }
  if (style.backgroundRepeat) {
    sx.backgroundRepeat = style.backgroundRepeat;
  }

  // Borders
  if (style.border) {
    const { width = 1, style: borderStyle = 'solid', color = 'divider' } = style.border;

    // Handle transparent border - set border to 'none' or width to 0
    if (!color || color === 'transparent' || color === 'rgba(0,0,0,0)' || color === 'rgba(0, 0, 0, 0)') {
      // For transparent borders, either set width to 0 or use 'transparent' keyword
      sx.border = 'none';
    } else {
      sx.border = width;
      sx.borderStyle = borderStyle;
      sx.borderColor = color;
    }
  }
  if (style.borderRadius !== undefined) {
    sx.borderRadius = typeof style.borderRadius === 'number' ? `${style.borderRadius}px` : style.borderRadius;
  }

  // Text and alignment
  if (style.textAlign) {
    sx.textAlign = style.textAlign;
  }
  if (style.verticalAlign) {
    sx.verticalAlign = style.verticalAlign;
  }
  if (style.justifyContent) {
    sx.justifyContent = style.justifyContent;
  }
  if (style.alignItems) {
    sx.alignItems = style.alignItems;
  }
  if (style.align) {
    // Legacy support - map to alignItems
    const alignMap = {
      'start': 'flex-start',
      'center': 'center',
      'end': 'flex-end'
    };
    sx.alignItems = alignMap[style.align as keyof typeof alignMap] || style.align;
  }

  // Dimensions
  if (style.minHeight !== undefined) {
    sx.minHeight = typeof style.minHeight === 'number' ? `${style.minHeight}px` : style.minHeight;
  }
  if (style.maxHeight !== undefined) {
    sx.maxHeight = typeof style.maxHeight === 'number' ? `${style.maxHeight}px` : style.maxHeight;
  }
  if (style.width !== undefined) {
    sx.width = typeof style.width === 'number' ? `${style.width}px` : style.width;
  }
  if (style.height !== undefined) {
    sx.height = typeof style.height === 'number' ? `${style.height}px` : style.height;
  }

  // Effects
  if (style.opacity !== undefined) {
    sx.opacity = style.opacity;
  }
  if (style.boxShadow) {
    sx.boxShadow = style.boxShadow;
  }

  // Add className if provided
  if (style.className) {
    sx.className = style.className;
  }

  return sx;
}

/**
 * Merges multiple sx props objects, with later ones taking precedence
 */
export function mergeSx(...sxProps: SxProps<Theme>[]): SxProps<Theme> {
  const result = sxProps.reduce((merged: any, current: any) => {
    if (!current) return merged;
    if (Array.isArray(current)) {
      return [...(Array.isArray(merged) ? merged : [merged]), ...current];
    }
    if (typeof current === 'object' && typeof merged === 'object' && !Array.isArray(merged)) {
      return { ...merged, ...current };
    }
    return current;
  }, {});
  
  return result as SxProps<Theme>;
}